import { Formik } from "formik";
import React, { useState, useCallback } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType, useAppSelector } from "@store/store";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sectionsIfOFC, sectionsNoOFC } from "@modules/stepper-row/components/rowStaticData";
import { Section, YesNoQuestion } from "@modules/stepper-row/types";
import { UtilityFormRenderer } from "@modules/stepper-row/components/UtilityFormRenderer";
import { setSiteRoadwaysROWField } from "@modules/stepper-row/redux/siteRoadwaysROWSlice";
import { setActiveStep } from "@modules/stepper-row/redux/stepperROWSlice";
import MuiButton from "@components/Button/MuiButton";
import { RemarksModal, RemarksAttachment } from "./RemarksModal";
import { postSeekClarificationResponseThunk } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { setAccessHoldPayload } from "@components/slider/redux/clarificationSlice";

export const RowQuestionMain: React.FC = React.memo(() => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const rowId = searchParams.get("row"); 

    // Remarks modal state
    const [remarksModalOpen, setRemarksModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState<any>(null);

    const siteRoadwaysROW = useSelector((state: RootReducerType) => state.siteRoadwaysROW,);

    const amenity = siteRoadwaysROW?.amenity;
    const utility_type = siteRoadwaysROW?.application_category;

    // ✅ NEW: read clarification data from Redux
    const clarificationData = useAppSelector(
        (state: any) => state.clarification.clarificationData,
    );

    // ✅ NEW: derive flags
    const QuestionsUpdateCheck = clarificationData?.questions_to_updated;
    const documentRequestNullCheck =
        clarificationData?.documents_to_updated?.length > 0;

    // ✅ NEW: clarification flag (moved AFTER QuestionsUpdateCheck is defined)
    const clarificationBothCase = QuestionsUpdateCheck && documentRequestNullCheck;

    // ✅ NEW: access_id — prefer clarification's application_id for the
    // clarification submit, otherwise fall back to the pre-existing value.
    const clarificationAccessId = clarificationData?.application_id;

    const sections = amenity === "OFC" ? sectionsIfOFC : sectionsNoOFC;

    // Get all yes/no questions from sections
    const getYesNoQuestions = (sections: Section[]): YesNoQuestion[] => {
        const questions: YesNoQuestion[] = [];

        const traverseFields = (fields: any[]) => {
            for (const field of fields) {
                if (field.type === "radio" && field.id !== undefined) {
                    questions.push({
                        id: field.id,
                        name: field.name,
                        label: field.title,
                    });
                }
                if (field.nestedFields && Array.isArray(field.nestedFields)) {
                    traverseFields(field.nestedFields);
                }
            }
        };

        for (const section of sections) {
            traverseFields(section.fields);
        }

        return questions;
    };

    const yesNoQuestions = getYesNoQuestions(sections);

    // Function to get all visible fields based on current form values
    const getVisibleFields = (values: Record<string, any>): string[] => {
        const visibleFields: string[] = [];

        const traverseFields = (fields: any[], parentValue?: string) => {
            for (const field of fields) {
                const isVisible = !parentValue || parentValue === "Yes";

                if (isVisible) {
                    if (field.name && field.type !== "radio") {
                        visibleFields.push(field.name);
                    }

                    if (field.type === "radio" && field.name) {
                        visibleFields.push(field.name);
                    }

                    if (field.nestedFields && Array.isArray(field.nestedFields)) {
                        const currentFieldValue = field.name
                            ? values[field.name]
                            : undefined;
                        traverseFields(field.nestedFields, currentFieldValue);
                    }
                }
            }
        };

        for (const section of sections) {
            traverseFields(section.fields);
        }

        const uniqueFields: string[] = [];
        visibleFields.forEach((field) => {
            if (!uniqueFields.includes(field)) {
                uniqueFields.push(field);
            }
        });

        return uniqueFields;
    };

    const {
        existing_duct_dimension_details,
        duct_utility_details,
        ofc_damage_details,
        right_of_use,
        utilized_nh_land_area,
        proposed_utility_dimension,
        circle_rate,
        available_row_width_left,
        available_row_width_right,
        proposed_utility_distance_left,
        proposed_utility_distance_right,
        land_acquisition_proposal_left,
        land_acquisition_proposal_right,
        total_land_to_be_acquired,
        already_laid_services_desc,
        number_of_existing_lanes,
        left_side_center_line,
        right_side_center_line,
        proposed_service_road_left,
        proposed_service_road_right,
        cable_position_within_right_of_way,
        utility_crossing_arrangement_id,
        cable_encasement_type,
        utility_line_casing_type,
        casing_depth_meters,
        waterway_prevention_compliance,
        laying_methodology,
        trench_refilling_methodology,
        trench_width_over_pipe_cm,
        trench_bedding_depth_cm,
        side_fill_layer_thickness_cm,
        distance_from_duct_edge_within_corridor,
        distance_from_edge_within_corridor,
        questions_true,
        questions_false,
    } = siteRoadwaysROW || {};

    const initialValues: Record<string, any> = {
        ...yesNoQuestions.reduce(
            (acc, question) => {
                if (questions_true?.includes(question.id)) {
                    acc[question.name] = "Yes";
                } else if (questions_false?.includes(question.id)) {
                    acc[question.name] = "No";
                } else {
                    acc[question.name] = "";
                }
                return acc;
            },
            {} as Record<string, any>,
        ),

        existing_duct_dimension_details,
        duct_utility_details,
        ofc_damage_details,
        right_of_use,
        utilized_nh_land_area,
        proposed_utility_dimension,
        circle_rate,
        available_row_width_left,
        available_row_width_right,
        proposed_utility_distance_left,
        proposed_utility_distance_right,
        land_acquisition_proposal_left,
        land_acquisition_proposal_right,
        total_land_to_be_acquired,
        already_laid_services_desc,
        number_of_existing_lanes,
        left_side_center_line,
        right_side_center_line,
        proposed_service_road_left,
        proposed_service_road_right,
        cable_position_within_right_of_way,
        utility_crossing_arrangement_id,
        cable_encasement_type,
        utility_line_casing_type,
        casing_depth_meters,
        waterway_prevention_compliance,
        laying_methodology,
        trench_refilling_methodology,
        trench_width_over_pipe_cm,
        trench_bedding_depth_cm,
        side_fill_layer_thickness_cm,
        distance_from_duct_edge_within_corridor,
        distance_from_edge_within_corridor,
        questions_true,
        questions_false,
    };

    const backButtonClickHandler = () => {
        navigate(-1);
    };

    // Function to get all field definitions (memoized to avoid recalculation)
    const getAllFieldDefinitions = React.useMemo(() => {
        const allFields: any[] = [];

        const traverseFields = (fields: any[]) => {
            for (const field of fields) {
                allFields.push(field);
                if (field.nestedFields && Array.isArray(field.nestedFields)) {
                    traverseFields(field.nestedFields);
                }
            }
        };

        for (const section of sections) {
            traverseFields(section.fields);
        }

        return allFields;
    }, [sections]);

    // Custom validation function
    const validateForm = React.useCallback(
        (values: Record<string, any>) => {
            const errors: Record<string, string> = {};
            const visibleFields = getVisibleFields(values);

            const fieldMap = new Map();
            getAllFieldDefinitions.forEach((field) => {
                if (field.name) {
                    fieldMap.set(field.name, field);
                }
            });

            visibleFields.forEach((fieldName) => {
                const value = values[fieldName];
                const fieldDef = fieldMap.get(fieldName);

                if (fieldDef && fieldDef.required !== false) {
                    if (fieldDef.type === "radio" && !value) {
                        errors[fieldName] = "Required";
                    } else if (
                        (fieldDef.type === "text" || fieldDef.type === "select") &&
                        !value
                    ) {
                        errors[fieldName] = "Required";
                    }
                }

                if (
                    fieldDef &&
                    fieldDef.dataType === "number" &&
                    value &&
                    isNaN(Number(value))
                ) {
                    errors[fieldName] = "Must be a number";
                }
            });

            return errors;
        },
        [getAllFieldDefinitions],
    );

    // ============ Build the payload (extracted for reuse) ============
    const buildPayload = useCallback(
        (
            values: any,
            remarks?: string,
            attachments?: RemarksAttachment[],
        ) => {
            const questions_true: number[] = [];
            const questions_false: number[] = [];

            yesNoQuestions.forEach(({ id, name }) => {
                const answer = values[name];
                if (answer === "Yes") {
                    questions_true.push(id);
                } else if (answer === "No") {
                    questions_false.push(id);
                }
            });

            const extractTextAndSelectFields = (
                sections: Section[],
                values: any,
            ): Record<string, any> => {
                const payload: Record<string, any> = {};

                const traverseFields = (fields: any[]) => {
                    for (const field of fields) {
                        if (field.type === "radio" && field.id !== undefined) {
                            if (field.nestedFields && Array.isArray(field.nestedFields)) {
                                traverseFields(field.nestedFields);
                            }
                            continue;
                        }

                        if (
                            field.type === "radio" &&
                            field.name &&
                            field.id === undefined
                        ) {
                            const value = values[field.name];
                            if (value && value !== "0" && value !== 0) {
                                payload[field.name] = value;
                            }
                        }

                        if (field.type === "text" || field.type === "select") {
                            const rawValue = values[field.name];
                            const processedValue =
                                field.dataType === "number" ? Number(rawValue) : rawValue;
                            const isBlank =
                                processedValue === "" ||
                                processedValue === null ||
                                processedValue === undefined ||
                                (typeof processedValue === "number" &&
                                    (processedValue === 0 || isNaN(processedValue))) ||
                                processedValue === "0";
                            if (!isBlank) {
                                payload[field.name] = processedValue;
                            }
                        }

                        if (field.nestedFields && Array.isArray(field.nestedFields)) {
                            traverseFields(field.nestedFields);
                        }
                    }
                };

                for (const section of sections) {
                    traverseFields(section.fields);
                }

                return payload;
            };

            const inputPayload = extractTextAndSelectFields(sections, values);
            const stringifiedInputPayload = Object.fromEntries(
                Object.entries(inputPayload)
                    .filter(([, value]) => {
                        const str = String(value);
                        return (
                            value !== null &&
                            value !== undefined &&
                            str !== "" &&
                            str !== "0"
                        );
                    })
                    .map(([key, value]) => [key, String(value)]),
            );

            // ============================================================
            // ✅ CLARIFICATION PATH (access-style payload)
            // ============================================================
            if (QuestionsUpdateCheck) {
                const clarificationPayload: Record<string, any> = {
                    application_id: clarificationData?.application_id,
                    recipient_id: clarificationData?.sender_details?.sender_id,
                    recipient_role: clarificationData?.sender_role,

                    step2_details: {
                        questions_true,
                        questions_false,
                        ...stringifiedInputPayload,
                    },

                    // ✅ remarks
                    ...(remarks ? { message: remarks } : {}),

                    // ✅ attachments → clk_documents
                    ...(attachments && attachments.length > 0
                        ? {
                            clk_documents: attachments.map((file) => ({
                                fileName: file.fileName,
                                originalFileName: file.originalFileName,
                                objectKey: file.objectKey,
                                mimeType: file.mimeType || "pdf",
                            })),
                        }
                        : {}),
                };

                return clarificationPayload;
            }

            // ============================================================
            // ✅ DEFAULT (existing) PATH
            // ============================================================
            const finalPayload: Record<string, any> = {
                application_id: clarificationAccessId,
                utility_type: utility_type,
                questions_true,
                questions_false,
                ...stringifiedInputPayload,
            };

            if (remarks) {
                finalPayload.remarks = remarks;
            }

            return finalPayload;
        },
        [
            yesNoQuestions,
            sections,
            clarificationAccessId,
            utility_type,
            clarificationBothCase,
            clarificationData,
        ],
    );

    // ============ Navigation helpers ============
    const navigateToUpdateDocuments = useCallback(() => {
        // ✅ Preserve row param (removed stray `}` from original)
        navigate(`/update-documents?row=${clarificationData?.application_id}`);
    }, [navigate, clarificationData?.application_id]);

    // ============ Core submit logic ============
    const performSubmit = useCallback(
        async (values: any, remarks?: string, attachments?: RemarksAttachment[]) => {
            setIsSubmitting(true);
            try {
                const finalPayload = buildPayload(values, remarks, attachments);
                const response = await dispatch(
                    postSeekClarificationResponseThunk(finalPayload),
                );
                if (response.payload.success) {
                    toast.success("Access Amenities Information saved successfully");
                    if (!clarificationBothCase) navigate("/application-details-row", { replace: true })
                } else {
                    toast.error(
                        response?.payload?.message || "Failed to save application",
                    );
                }
            } catch (error) {
                toast.error("An error occurred while submitting the form");
            } finally {
                setIsSubmitting(false);
            }
        },
        [dispatch, buildPayload, navigateToUpdateDocuments],
    );

    // ============ Handle submit button click ============
    const handleSubmitClick = useCallback(
        (values: any) => {
            if (!clarificationBothCase) {
                // ✅ clarification === true → existing flow: remarks modal → real API call
                setPendingFormValues(values);
                setRemarksModalOpen(true);
            } else {
                const stashedPayload = buildPayload(values);
                dispatch(
                    setAccessHoldPayload({
                        step2_details: stashedPayload.step2_details,
                    }),
                );
                navigateToUpdateDocuments();
            }
        },
        [
            clarificationBothCase,
            buildPayload,
            dispatch,
            navigateToUpdateDocuments,
        ]
    );

    // ============ Handle remarks modal submit ============
    const handleRemarksSubmit = useCallback(
        (remarks: string, attachments: RemarksAttachment[]) => {
            setRemarksModalOpen(false);
            if (pendingFormValues) {
                performSubmit(pendingFormValues, remarks, attachments);
                setPendingFormValues(null);
            }
        },
        [pendingFormValues, performSubmit],
    );

    const handleRemarksModalClose = useCallback(() => {
        setRemarksModalOpen(false);
        setPendingFormValues(null);
    }, []);

    return (
        <>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validate={validateForm}
                onSubmit={async (values: any) => {
                    handleSubmitClick(values);
                }}
            >
                {({
                    handleSubmit,
                    isValid,
                    dirty,
                    errors,
                    values,
                    setFieldValue,
                    handleChange,
                }) => {
                    const visibleFields = getVisibleFields(values);
                    const fieldMap = new Map();

                    getAllFieldDefinitions.forEach((field) => {
                        if (field.name) {
                            fieldMap.set(field.name, field);
                        }
                    });

                    let isFormValid = true;

                    for (const fieldName of visibleFields) {
                        const fieldDef = fieldMap.get(fieldName);
                        const value = values[fieldName];

                        if (fieldDef && fieldDef.required !== false) {
                            if (fieldDef.type === "radio" && !value) {
                                isFormValid = false;
                                break;
                            } else if (
                                (fieldDef.type === "text" ||
                                    fieldDef.type === "select") &&
                                !value
                            ) {
                                isFormValid = false;
                                break;
                            }
                        }

                        if (
                            fieldDef &&
                            fieldDef.dataType === "number" &&
                            value &&
                            isNaN(Number(value))
                        ) {
                            isFormValid = false;
                            break;
                        }
                    }

                    return (
                        <Box
                            className="amenities-container"
                            component="div"
                            sx={{ height: "calc(100vh - 11rem)", overflowY: "auto" }}
                        >
                            <Box className="form-wrapper" sx={{ paddingTop: 0 }}>
                                <form onSubmit={handleSubmit}>
                                    <UtilityFormRenderer
                                        sections={sections}
                                        handleFieldChange={(name, value) => {
                                            setFieldValue(name, value);
                                            dispatch(
                                                setSiteRoadwaysROWField({
                                                    name,
                                                    value,
                                                }),
                                            );

                                            const updatedValues = {
                                                ...values,
                                                [name]: value,
                                            };
                                            const updatedQuestionsTrue: number[] = [];
                                            const updatedQuestionsFalse: number[] = [];

                                            yesNoQuestions.forEach(
                                                ({ id, name: qName }) => {
                                                    const answer =
                                                        qName === name
                                                            ? value
                                                            : updatedValues[qName];
                                                    if (answer === "Yes") {
                                                        updatedQuestionsTrue.push(id);
                                                    } else if (answer === "No") {
                                                        updatedQuestionsFalse.push(id);
                                                    }
                                                },
                                            );

                                            dispatch(
                                                setSiteRoadwaysROWField({
                                                    name: "questions_true",
                                                    value: updatedQuestionsTrue,
                                                }),
                                            );
                                            dispatch(
                                                setSiteRoadwaysROWField({
                                                    name: "questions_false",
                                                    value: updatedQuestionsFalse,
                                                }),
                                            );
                                        }}
                                    />
                                </form>
                            </Box>
                            <Box
                                className="stepper-btns gray:br-t-500 mt-3 py-4 flex gap-4 mr-4"
                                sx={{ justifyContent: "flex-end" }}
                            >
                                <MuiButton
                                    type="button"
                                    name="Cancel"
                                    variant="outlined"
                                    fullWidth
                                    onClick={backButtonClickHandler}
                                    sx={{
                                        border: "1px solid #3A1C73",
                                        color: `#3A1C73`,
                                        borderRadius: "50px",
                                        fontSize: "1rem",
                                        fontWeight: `var(--font-regular)`,
                                        width: "fit-content",
                                        height: "unset",
                                        transition: "all 0.3s",
                                        "&:hover": {
                                            background: `#3A1C73`,
                                            color: "#fff",
                                        },
                                    }}
                                />
                                <MuiButton
                                    type="button"
                                    name="Submit"
                                    variant="outlined"
                                    fullWidth
                                    disabled={!isValid || isSubmitting}
                                    onClick={() => handleSubmit()}
                                    sx={{
                                        border: "1px solid #3A1C73",
                                        color: `#fff !important`,
                                        borderRadius: "50px",
                                        fontSize: "1rem",
                                        fontWeight: `var(--font-regular)`,
                                        width: "fit-content",
                                        height: "unset",
                                        background: `#3A1C73`,
                                        transition: "all 0.3s",
                                        "&:disabled": {
                                            opacity: 0.6,
                                            cursor: "not-allowed",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                }}
            </Formik>

            {/* Remarks Modal */}
            <RemarksModal
                open={remarksModalOpen}
                onClose={handleRemarksModalClose}
                onSubmit={handleRemarksSubmit}
                loading={isSubmitting}
                accessId={rowId}
            />
        </>
    );
});